import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";

const SERVICES = ["Air Freight", "Sea Freight", "Warehousing", "Trucking", "Project Logistics", "Dangerous Goods", "Not sure yet"];

const Field = ({ label, name, value, onChange, type = "text", required, testid, placeholder }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">{label}{required && " *"}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-white/15 py-3 text-lg text-white placeholder-white/20 focus:border-primary focus:outline-none transition-colors duration-300"
    />
  </label>
);

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", service: "Air Freight", origin: "", destination: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Quote request received. Our team will reach out within 24h.");
      setForm({ name: "", email: "", company: "", phone: "", service: "Air Freight", origin: "", destination: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="border border-primary/40 bg-primary/5 p-10">
        <p className="font-mono text-xs uppercase tracking-widest text-primary">Request received</p>
        <h3 className="font-display text-3xl uppercase mt-4">Thank you.</h3>
        <p className="text-white/60 mt-3 max-w-md">Our logistics desk will review your shipment and respond within 24 hours with a tailored quote.</p>
        <button onClick={() => setDone(false)} data-testid="quote-reset" className="mt-6 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-primary transition-colors">Submit another →</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-8">
        <Field label="Full name" name="name" value={form.name} onChange={onChange} required testid="quote-name" placeholder="Jane Cargo" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="quote-email" placeholder="jane@company.com" />
        <Field label="Company" name="company" value={form.company} onChange={onChange} testid="quote-company" placeholder="Acme Imports" />
        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="quote-phone" placeholder="+1 …" />
      </div>

      <label className="block">
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">Service *</span>
        <select
          name="service"
          value={form.service}
          onChange={onChange}
          data-testid="quote-service"
          className="mt-2 w-full bg-[#0c0c0c] border-b border-white/15 py-3 text-lg text-white focus:border-primary focus:outline-none transition-colors duration-300"
        >
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <div className="grid sm:grid-cols-2 gap-8">
        <Field label="Origin" name="origin" value={form.origin} onChange={onChange} testid="quote-origin" placeholder="Shanghai, CN" />
        <Field label="Destination" name="destination" value={form.destination} onChange={onChange} testid="quote-destination" placeholder="Los Angeles, US" />
      </div>

      <label className="block">
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">Shipment details</span>
        <textarea
          name="message"
          value={form.message}
          onChange={onChange}
          rows={4}
          data-testid="quote-message"
          placeholder="Cargo type, weight, volume, timeline…"
          className="mt-2 w-full bg-transparent border-b border-white/15 py-3 text-lg text-white placeholder-white/20 focus:border-primary focus:outline-none transition-colors duration-300 resize-none"
        />
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="quote-submit"
        className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest disabled:opacity-50"
      >
        {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Request Quote <ArrowUpRight size={16} /></>}
      </button>
    </form>
  );
};
